import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, addDoc, updateDoc, doc,
  query, where, orderBy, Timestamp, onSnapshot
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { Spin } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
dayjs.extend(relativeTime);
dayjs.locale("fr");

export default function MessagesPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [fullName, setFullName] = useState("");
  const [uid, setUid] = useState("");
  const repliesEndRef = useRef(null);

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };
  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    setUid(user.uid);

    const init = async () => {
      // Get fullname
      try {
        const cSnap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
        if (!cSnap.empty) setFullName(cSnap.docs[0].data().fullname || cSnap.docs[0].data().name || "");
      } catch {}
    };
    init();

    // Real-time listener for messages
    const q = query(collection(db, "messages"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter: announcements (to == "all") + direct to me
      const mine = all.filter(m =>
        m.to === "all" || m.to === user.uid
      );
      setMessages(mine);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Load replies when message selected
  useEffect(() => {
    if (!selected) { setReplies([]); return; }

    // Mark as read
    markRead(selected);

    const q = query(
      collection(db, "messages_replies"),
      where("messageId", "==", selected.id),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, snap => {
      setReplies(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => repliesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
    return () => unsub();
  }, [selected?.id]);

  const markRead = async (msg) => {
    if (!msg || !uid) return;
    try {
      if (msg.to === "all") {
        const alreadyRead = (msg.readBy || []).includes(uid);
        if (!alreadyRead) {
          await updateDoc(doc(db, "messages", msg.id), {
            readBy: [...(msg.readBy || []), uid],
          });
        }
      } else if (msg.to === uid && !msg.read) {
        await updateDoc(doc(db, "messages", msg.id), { read: true });
      }
    } catch {}
  };

  const isUnread = (msg) => {
    if (!uid) return false;
    if (msg.to === "all") return !(msg.readBy || []).includes(uid);
    return msg.to === uid && !msg.read;
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await addDoc(collection(db, "messages_replies"), {
        messageId: selected.id,
        from:      uid,
        fromName:  fullName,
        body:      replyText.trim(),
        createdAt: Timestamp.fromDate(new Date()),
        read:      false,
      });
      setReplyText("");
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const unreadCount = messages.filter(isUnread).length;

  const typeIcon = (msg) => msg.type === "announcement" ? "📢" : "💬";
  const typeLabel = (msg) => msg.type === "announcement" ? "Annonce" : msg.to === "all" ? "Général" : "Message privé";

  if (selected) {
    return (
      <div style={{ background: t.bg, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", height: "100vh" }}>

          {/* Header */}
          <div style={{ padding: "16px 18px", borderBottom: `1px solid ${t.border}`, flexShrink: 0, background: t.cardBg }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => setSelected(null)} style={{ background: t.bg3, border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, color: t.text2 }}>← Retour</button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.subject}</div>
                <div style={{ fontSize: 10, color: t.text3 }}>{typeIcon(selected)} {typeLabel(selected)} · {dayjs(selected.createdAt?.toDate?.()).fromNow()}</div>
              </div>
            </div>
          </div>

          {/* Thread */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>

            {/* Original message */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: t.blueBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                🏢
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.text3, marginBottom: 4 }}>
                  <strong style={{ color: t.text2 }}>WeStudy Admin</strong> · {dayjs(selected.createdAt?.toDate?.()).format("DD/MM HH:mm")}
                </div>
                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "4px 14px 14px 14px", padding: "12px 14px", fontSize: 13, color: t.text1, lineHeight: 1.6 }}>
                  {selected.body}
                </div>
              </div>
            </div>

            {/* Replies */}
            {replies.map(reply => {
              const isMe = reply.from === uid;
              return (
                <div key={reply.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", flexDirection: isMe ? "row-reverse" : "row" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: isMe ? t.blueBg : t.bg3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: isMe ? t.blue : t.text2, flexShrink: 0 }}>
                    {isMe ? (fullName?.[0] || "M") : "🏢"}
                  </div>
                  <div style={{ maxWidth: "75%" }}>
                    <div style={{ fontSize: 11, color: t.text3, marginBottom: 4, textAlign: isMe ? "right" : "left" }}>
                      <strong style={{ color: t.text2 }}>{isMe ? "Vous" : reply.fromName || "Admin"}</strong> · {dayjs(reply.createdAt?.toDate?.()).format("DD/MM HH:mm")}
                    </div>
                    <div style={{
                      background: isMe ? t.blue : t.cardBg,
                      border: isMe ? "none" : `1px solid ${t.border}`,
                      borderRadius: isMe ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                      padding: "10px 14px", fontSize: 13,
                      color: isMe ? "#fff" : t.text1, lineHeight: 1.6,
                    }}>
                      {reply.body}
                    </div>
                  </div>
                </div>
              );
            })}

            <div ref={repliesEndRef} />
          </div>

          {/* Reply input — only for support messages */}
          {selected.type !== "announcement" && (
            <div style={{ padding: "12px 18px 24px", borderTop: `1px solid ${t.border}`, background: t.cardBg, flexShrink: 0 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleReply()}
                  placeholder="Répondre..."
                  style={{ flex: 1, height: 42, borderRadius: 21, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, padding: "0 16px", fontSize: 13, outline: "none" }}
                />
                <button onClick={handleReply} disabled={!replyText.trim() || sending}
                  style={{ width: 42, height: 42, borderRadius: "50%", border: "none", background: !replyText.trim() ? t.bg3 : t.blue, color: !replyText.trim() ? t.text3 : "#fff", fontSize: 18, cursor: !replyText.trim() ? "default" : "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {sending ? "..." : "→"}
                </button>
              </div>
              <div style={{ fontSize: 10, color: t.text3, textAlign: "center", marginTop: 6 }}>Appuyez sur Entrée pour envoyer</div>
            </div>
          )}

          {selected.type === "announcement" && (
            <div style={{ padding: "10px 18px 24px", borderTop: `1px solid ${t.border}`, background: t.cardBg, flexShrink: 0, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: t.text3 }}>📢 Annonce officielle WeStudy — lecture seule</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1, display: "flex", alignItems: "center", gap: 6 }}>
                Messages
                {unreadCount > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, background: t.red, color: "#fff", borderRadius: 20, padding: "1px 7px", minWidth: 18, textAlign: "center" }}>
                    {unreadCount}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: t.text2 }}>{messages.length} message{messages.length !== 1 ? "s" : ""}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}><Spin /></div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 18px", color: t.text3 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: t.text2 }}>Aucun message</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>L'administration vous contactera ici.</div>
          </div>
        ) : (
          <div style={{ padding: "16px 18px 0", display: "flex", flexDirection: "column", gap: 10 }}>

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 8 }}>
              {["Tous", "Annonces", "Messages"].map((tab, i) => (
                <button key={tab} onClick={() => {}}
                  style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${i === 0 ? t.blue : t.border}`, background: i === 0 ? t.blueBg : t.bg3, color: i === 0 ? t.blueText : t.text2, fontSize: 12, cursor: "pointer" }}>
                  {tab}
                </button>
              ))}
            </div>

            {messages.map(msg => {
              const unread = isUnread(msg);
              return (
                <div key={msg.id} onClick={() => setSelected(msg)}
                  style={{
                    background: t.cardBg, border: `1.5px solid ${unread ? t.blue : t.border}`,
                    borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                    transition: "border-color 0.15s",
                    boxShadow: unread ? `0 2px 12px rgba(37,99,235,0.08)` : "none",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = t.blue}
                  onMouseLeave={e => e.currentTarget.style.borderColor = unread ? t.blue : t.border}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    {/* Icon */}
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: msg.type === "announcement" ? t.amberBg : t.blueBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                      {typeIcon(msg)}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <div style={{ fontSize: 13, fontWeight: unread ? 700 : 500, color: t.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                          {msg.subject}
                        </div>
                        <div style={{ fontSize: 10, color: t.text3, flexShrink: 0 }}>
                          {dayjs(msg.createdAt?.toDate?.()).fromNow()}
                        </div>
                      </div>

                      <div style={{ fontSize: 12, color: t.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 6 }}>
                        {msg.body}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: msg.type === "announcement" ? t.amberBg : t.blueBg, color: msg.type === "announcement" ? t.amberText : t.blueText, fontWeight: 500 }}>
                          {typeLabel(msg)}
                        </span>
                        {unread && (
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: t.blue, display: "inline-block" }} />
                        )}
                        {msg.to === "all" && (
                          <span style={{ fontSize: 10, color: t.text3 }}>👥 Tous les membres</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const theme = {
  light: { bg:"#f8fafc",bg3:"#f1f5f9",border:"#e2e8f0",text1:"#0f172a",text2:"#64748b",text3:"#94a3b8",blue:"#2563eb",blueBg:"#eff6ff",blueText:"#1d4ed8",green:"#16a34a",greenBg:"#f0fdf4",amber:"#d97706",amberBg:"#fffbeb",amberText:"#b45309",amberBorder:"#fde68a",red:"#dc2626",redBg:"#fef2f2",redBorder:"#fecaca",cardBg:"#ffffff" },
  dark:  { bg:"#0f172a",bg3:"#1a2740",border:"#273549",text1:"#f1f5f9",text2:"#94a3b8",text3:"#475569",blue:"#378ADD",blueBg:"#1e3a5f",blueText:"#85B7EB",green:"#4ade80",greenBg:"#052e16",amber:"#fbbf24",amberBg:"#2a1e07",amberText:"#fcd34d",amberBorder:"#78350f",red:"#f87171",redBg:"#2d1515",redBorder:"#7f1d1d",cardBg:"#1e293b" },
};

const s = {
  toggleTrack: { width:36,height:20,borderRadius:20,cursor:"pointer",position:"relative",flexShrink:0 },
  toggleKnob:  { width:14,height:14,borderRadius:"50%",position:"absolute",top:2,left:2,transition:"transform 0.2s" },
};
